// contexts/AuthContext.tsx

import React, { createContext, useState, useContext, useEffect, useRef, ReactNode } from 'react';
import { Company } from '../types/company';
import { User } from '../types/user';
import { authAPI } from '../services/api';
import { indexedDBStorage } from '../services/storage/indexedDB';
import { openAuthGate, resetAuthGate } from '../services/token-manager';
import { setAuthDiag } from '../utils/errorLogger';
import { SINGLE_LOAD_ENABLED } from '../config';

// ---------------------------------------------------------------------------
// 単一ロード（Flutterハンドシェイク）ヘルパー
// ---------------------------------------------------------------------------
// 新Flutter（単一ロード対応）は起動時に window.__flutterSingleLoad=true を立て、
// 認証データを IndexedDB へ注入したら window.__flutterAuthInjected=true をセットし、
// （Web が先に待ち受けを用意していれば）window.__flutterAuthReadyResolve() を呼ぶ。
// これにより Web は「注入完了」を待ってから復元でき、/login 往復（二重フルロード）を
// 発生させずに1回ロードで済む。旧Flutter/ブラウザはフラグが無いので即座に進む（従来動作）。
function isSingleLoadFlutter(): boolean {
  try {
    return !!(window as any).__flutterSingleLoad;
  } catch {
    return false;
  }
}

// 注入完了待ちの上限。これを過ぎたら注入無しとみなして復元へ進む（=失敗時は /login へ落ち、
// 新Flutter側の従来 /login 注入フォールバックが働くため、ハングも取りこぼしも起きない）。
const FLUTTER_AUTH_WAIT_MS = 4000;

function waitForFlutterAuthInjection(timeoutMs: number): Promise<void> {
  const w = window as any;
  if (w.__flutterAuthInjected) return Promise.resolve();
  return new Promise<void>((resolve) => {
    let done = false;
    let poll: ReturnType<typeof setInterval> | undefined;
    let hard: ReturnType<typeof setTimeout> | undefined;
    const finish = () => {
      if (done) return;
      done = true;
      if (poll) clearInterval(poll);
      if (hard) clearTimeout(hard);
      resolve();
    };
    // Flutter が注入直後に呼ぶ（早い/遅いどちらの順序でも取りこぼさないよう二重化）。
    w.__flutterAuthReadyResolve = finish;
    const start = Date.now();
    poll = setInterval(() => {
      if (w.__flutterAuthInjected || Date.now() - start > timeoutMs) finish();
    }, 50);
    hard = setTimeout(finish, timeoutMs);
  });
}

// 認証コンテキストの型定義
interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  authorityResolved: boolean;
  availableCompanies: Company[];
  hasSelectedCompany: boolean;
  getCompanyList: (loginid: string, password: string) => Promise<{success: boolean, companyList?: Company[], message?: string}>;
  login: (loginid: string, password: string, companyid: string) => Promise<{success: boolean, message?: string}>;
  refreshToken: () => Promise<{success: boolean, message?: string}>;
  logout: () => void;
}

// 認証プロバイダーのプロップス
interface AuthProviderProps {
  children: ReactNode;
}

// コンテキストの作成
const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  // ロール権限（authoritylist）の取得（getLoginUserInfo）が完了したか。
  // restoreSession は保存済みユーザーで先に描画し、authoritylist は後追いで取得するため、
  // 取得前の一瞬は「権限なし」に見える。リリースビルドは描画が速く、この一瞬に
  // MenuPage の「再ログインが必要」バナーが誤って出てしまう（デバッグ/ブラウザでは
  // 描画が遅く取得が先に終わるため出ない）。取得が確定するまでバナー判定を保留するための旗。
  const [authorityResolved, setAuthorityResolved] = useState<boolean>(false);
  const [availableCompanies, setAvailableCompanies] = useState<Company[]>([]);
  const [hasSelectedCompany, setHasSelectedCompany] = useState<boolean>(false);
  // セッション世代。login / logout のたびに加算し、復元時に走らせたバックグラウンド
  // 権限取得が解決した時、別セッションへ切り替わっていれば反映を破棄するために使う。
  const sessionEpochRef = useRef(0);

  // セッション状態を復元する関数
  const restoreSession = async () => {
    try {
      // 新Flutter（単一ロード）配下では、Flutterの認証注入完了を待ってから IndexedDB を読む。
      // これにより /login への往復（＝二重フルロード）を発生させず1回ロードで復元できる。
      // 旧Flutter/ブラウザではフラグが無いので即座に進む（従来動作）。
      if (SINGLE_LOAD_ENABLED && isSingleLoadFlutter()) {
        setAuthDiag({ bootPath: 'single' });
        await waitForFlutterAuthInjection(FLUTTER_AUTH_WAIT_MS);
      } else {
        setAuthDiag({ bootPath: isSingleLoadFlutter() ? 'single-disabled' : 'legacy' });
      }

      // IndexedDBからトークンとユーザーデータを取得
      let token = await indexedDBStorage.getItem('token');
      let userData = await indexedDBStorage.getItem('user');
      
      // データがなければ認証失敗
      if (!token || !userData) {
        return false;
      }

      // トークンをリフレッシュして有効化する。
      // Flutter は初回ログイン時のトークン（古い場合あり）を毎回注入するため、
      // 起動時にリフレッシュしないと期限切れトークンのまま API を叩いて失敗する
      // （acs-l.jp への 401 が CORS で TypeError: Load failed に化ける）。
      //
      // ただし毎回リフレッシュすると、画面を素早く行き来した際に単回使用の
      // リフレッシュトークンが競合する。そこで「直近5分以内にリフレッシュ済みなら
      // スキップ」する。Flutter は注入時に webTokenRefreshedAt を消すため、起動直後は
      // 必ず1回リフレッシュされる。タイムアウト付き・失敗してもセッションは破棄しない。
      const REFRESH_SKIP_WINDOW = 5 * 60 * 1000; // 5分
      const lastRefreshStr = await indexedDBStorage.getItem('webTokenRefreshedAt');
      const recentlyRefreshed =
        !!lastRefreshStr &&
        Date.now() - parseInt(lastRefreshStr, 10) < REFRESH_SKIP_WINDOW;

      if (!recentlyRefreshed) {
        let refreshed = false;
        try {
          const result = await Promise.race([
            authAPI.refreshToken(),
            new Promise<{ success: boolean }>((resolve) =>
              setTimeout(() => resolve({ success: false }), 8000),
            ),
          ]);
          refreshed = result.success;
        } catch (_) {
          refreshed = false;
        }
        if (refreshed) {
          // リフレッシュ成功時刻を記録（連続リロードでの再リフレッシュ競合を防ぐ）
          await indexedDBStorage.setItem(
            'webTokenRefreshedAt',
            Date.now().toString(),
          );
        }
        // 失敗してもセッションは破棄しない（既存トークンで続行し、白画面/ログイン
        // 戻りループを防ぐ。本当に無効なら各 API の応答で処理される）。
      }

      // ユーザー情報を復元
      const parsedUser: User = JSON.parse(userData);

      // 先に保存済みのユーザー情報で描画を確定する（権限取得の通信で起動をブロックしない）
      setUser(parsedUser);
      setIsAuthenticated(true);

      // 企業が選択されているか確認
      const selectedCompany = parsedUser.selectedCompany;
      if (selectedCompany) {
        setHasSelectedCompany(true);
      }

      // ロール権限はバックグラウンドで最新化し、取得できたら差し替える（描画はブロックしない）
      // 取得待ちの間に logout / 別アカウントログインが起きた場合は、セッション世代が
      // 変わっているので反映しない（ログアウト済みセッションの復活・別アカウントへの権限の取り違えを防ぐ）。
      const restoreEpoch = sessionEpochRef.current;
      // getLoginUserInfo は起動直後のトークン受け渡し窓（Flutter 注入待ち・並列APIバースト）で
      // 一時的に失敗しやすい。1回で失敗確定すると authoritylist が未取得（null）のまま
      // authorityResolved=true になり、権限があるユーザーにまで /menu で「権限なし」バナーが
      // 誤表示される（診断ログ diag.menuAuthBanner の主因）。そこで短いバックオフで数回だけ
      // 再試行し、取得できたら差し替える。成功応答（空権限含む）は確定的な答えとして即採用する。
      const AUTHORITY_FETCH_ATTEMPTS = 3;
      const resolveAuthority = async () => {
        for (let attempt = 0; attempt < AUTHORITY_FETCH_ATTEMPTS; attempt++) {
          try {
            // 中間試行はログを抑止し、最終試行のみ失敗を記録する（同一失敗の多重ログ回避）。
            const isLastAttempt = attempt === AUTHORITY_FETCH_ATTEMPTS - 1;
            const res = await authAPI.getLoginUserInfo(undefined, isLastAttempt);
            if (sessionEpochRef.current !== restoreEpoch) return; // 世代交代したら反映しない
            if (res.success && res.userInfo) {
              const updatedUser: User = {
                ...parsedUser,
                authoritylist: res.userInfo.authoritylist ?? [],
                specialuser: res.userInfo.specialuser ?? false,
              };
              setUser(updatedUser);
              indexedDBStorage.setItem('user', JSON.stringify(updatedUser)).catch(() => {});
              // プローブ(getLoginUserInfo)が実際に成功＝サーバーへの接続が生きた確証。
              // ここで初めて認証ゲートを開き、/menu の並列データAPIを解放する。
              // （authorityResolved は失敗でも true になるため解放条件には使わない。
              //   死んだ接続へ一斉解放するとバーストするのを防ぐ。失敗時は保険TOで開く）
              openAuthGate('probe-success');
              return; // 取得成功（成功応答は空でも確定的な答えなので再試行しない）
            }
            // success=false は一時失敗の可能性があるため再試行対象
          } catch {
            // 例外（通信/トークン未準備）も再試行対象
          }
          if (sessionEpochRef.current !== restoreEpoch) return;
          // 最終試行の後は待たない。トークン注入の遅れを吸収するため漸増バックオフ（0.6s,1.2s）。
          if (attempt < AUTHORITY_FETCH_ATTEMPTS - 1) {
            await new Promise(resolve => setTimeout(resolve, 600 * (attempt + 1)));
          }
        }
      };
      resolveAuthority().finally(() => {
        // 再試行を尽くした後に「権限取得は完了した」ものとして判定を解禁する。
        // ここまでで取得できなかった場合、保存済みユーザーに authoritylist が無ければ
        // MenuPage 側が「取得失敗」として扱う（真の無権限とは区別する）。
        if (sessionEpochRef.current === restoreEpoch) setAuthorityResolved(true);
      });

      return true;
    } catch (error) {
      return false;
    }
  };

  // ローカルストレージから認証状態を復元
  useEffect(() => {
    const initializeAuth = async () => {
      setLoading(true);
      await restoreSession();
      setLoading(false);
    };

    initializeAuth();
  }, []);

  // Flutter(WebView) へ「アプリが表示可能になった」ことを一度だけ通知する。
  // Flutter は起動時の白オーバーレイをこの通知まで維持し、ルート→/login→注入→再読込→
  // 復元 の一連のちらつきをオーバーレイ裏に隠す（Flutter 側は保険タイマーでも解除する）。
  // 認証済みなら権限確定(authorityResolved)まで待って通知し、権限バナーの一瞬表示も隠す。
  // 未認証ならログイン画面を出すため復元完了時点で通知する。ブラウザでは Flutter 不在で無視される。
  const appReadyPostedRef = useRef(false);
  useEffect(() => {
    if (appReadyPostedRef.current) return;
    const ready = !loading && (!isAuthenticated || authorityResolved);
    if (!ready) return;
    appReadyPostedRef.current = true;
    // 認証ゲートの開放は「接続が生きた確証」を得てから行う（cold-start バースト抑止）。
    //  - 認証済み：プローブ(getLoginUserInfo)実成功時に resolveAuthority が
    //    openAuthGate('probe-success') 済み。ここでは開けない（失敗時は token-manager の
    //    保険タイマー AUTH_GATE_MAX_WAIT_MS で 'timeout' 解放される）。
    //  - 未認証：トークン不要でログイン画面の通信を止めないため、ここで即開放する。
    if (!isAuthenticated) {
      openAuthGate('unauth');
    }
    try {
      (window as any).FlutterAppReady?.postMessage('ready');
    } catch (_) {
      // Flutter 不在（ブラウザ）では無視
    }
  }, [loading, isAuthenticated, authorityResolved]);

  // エラーlog同梱用スナップショットに、現在のユーザー識別子・権限状態を反映する。
  // （お客様端末を見られないため、エラー発生時にどの userno がどの権限状態だったかを残す）
  useEffect(() => {
    setAuthDiag({
      userno: user?.userno,
      authorityResolved,
      isAuthenticated,
      userAuthorityCount: Array.isArray(user?.authoritylist)
        ? user!.authoritylist!.length
        : null,
      userSpecialuser: user?.specialuser ?? null,
    });
  }, [user, authorityResolved, isAuthenticated]);

  // 企業一覧を取得
  const getCompanyList = async (loginid: string, password: string) => {
    try {
      const result = await authAPI.getCompanyList(loginid, password);
      
      if (result.success && result.companyList) {
        // API から返される企業リストを設定
        // companyid と companyname が使われているため、そのまま設定する
        setAvailableCompanies(result.companyList);
      }
      
      return result;
    } catch (error) {
      return { success: false, message: '企業一覧の取得に失敗しました' };
    }
  };

  // ログイン処理
  const login = async (loginid: string, password: string, companyid: string) => {
    try {
      const result = await authAPI.login(loginid, password, companyid);
      
      if (result.success) {
        // セッションが切り替わったので、復元時のバックグラウンド権限取得は破棄させる
        sessionEpochRef.current++;
        setIsAuthenticated(true);
        // ログイン処理は authoritylist を取得済みのユーザーを返すため、判定を解禁する
        setAuthorityResolved(true);
        // ログイン成功＝トークン取得済み＆サーバー接続確認済み。認証ゲートを開放する。
        openAuthGate('login');

        // ユーザー情報がある場合のみsetUserを実行
        if (result.user) {
          setUser(result.user);
          
          // ユーザー情報をコンソールに出力
          
          if (result.user.selectedCompany && result.user.selectedCompany.companyid) {
            setHasSelectedCompany(true);
            // 企業IDをindexedDBに保存
            await indexedDBStorage.setItem('companyId', result.user.selectedCompany.companyid.toString());
          }
        }
        
        return { success: true };
      }
      
      return { success: false, message: result.message || 'ログインに失敗しました' };
    } catch (error) {
      return { success: false, message: 'ログインに失敗しました' };
    }
  };

  // トークンリフレッシュ
  const refreshToken = async () => {
    try {
      return await authAPI.refreshToken();
    } catch (error) {
      return { success: false, message: 'トークンの更新に失敗しました' };
    }
  };

  // ログアウト処理
  const logout = () => {
    // セッションが終了したので、復元時のバックグラウンド権限取得は破棄させる
    sessionEpochRef.current++;
    // 認証ゲートを閉じ直す（再ログイン時に改めてブートストラップを待たせるため）
    resetAuthGate();
    authAPI.logout();
    setIsAuthenticated(false);
    setAuthorityResolved(false);
    setUser(null);
    setHasSelectedCompany(false);
    setAvailableCompanies([]);
  };

  // token-manager（非Reactモジュール）から認証状態を落とせるようにフックを公開する。
  // ブラウザで /refresh が RT拒否(body 400)を返し自己回復不能になった際、requestWebReauth が
  // これを呼んで isAuthenticated を落としてから /login へ遷移する（/login→/menu バウンス防止）。
  useEffect(() => {
    (window as any).__appForceLogout = () => {
      logout();
    };
    return () => {
      try {
        delete (window as any).__appForceLogout;
      } catch {
        /* no-op */
      }
    };
  }, []);

  // コンテキストで提供する値
  const value: AuthContextType = {
    isAuthenticated,
    user,
    loading,
    authorityResolved,
    availableCompanies,
    hasSelectedCompany,
    getCompanyList,
    login,
    refreshToken,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

// カスタムフック
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};